import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api, structureReportPath, type StructureDetail, type StructureWrite } from '../lib/api';
import { clearUnsavedChanges } from '../lib/UnsavedChanges';
import { WhatToIncludeLink } from '../lib/WhatToIncludeLink';
import { HoverTooltip } from '../lib/HoverTooltip';

const MAX_NOTES = 160;
const MAX_NICKNAME = 120;
const MAX_PHOTOS = 10;

/**
 * Step 4 — additional notes & photos. Bootstrap 5 + Hyper theme markup
 * mirroring legacy `resources/views/structure/images.blade.php` and
 * `resources/jsx/AppPhoto.jsx`.
 */
export function StructureNotesPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const id = params.get('id')?.trim() || null;

  const [notes, setNotes] = useState('');
  const [nickname, setNickname] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const notesEditedRef = useRef(false);
  const nicknameEditedRef = useRef(false);

  useEffect(() => {
    if (!id) return;

    let active = true;
    api.get<StructureDetail>(`/api/v1/structures/${id}`)
      .then((current) => {
        if (!active) return;
        if (!notesEditedRef.current) setNotes(current.structureNotes ?? '');
        if (!nicknameEditedRef.current) setNickname(current.nickname ?? '');
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [id]);

  if (!id) {
    return (
      <div className="container-fluid">
        <div className="row mt-lg-3 mt-1 justify-content-center">
          <div className="col-12 col-xl-6 col-lg-8 text-center">
            <div className="alert alert-warning">Missing structure id.</div>
            <Link to="/newstructure" className="btn btn-sm btn-primary">
              Restart
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const continueToReport = async () => {
    setBusy(true);
    setError(null);
    try {
      const current = await api.get<StructureDetail>(`/api/v1/structures/${id}`);
      const body: StructureWrite = {
          nickname: nicknameEditedRef.current ? nickname.trim() || null : current.nickname,
          permanent: current.permanent,
          oilFiltration: current.oilFiltration,
          structureTypeId: current.structureTypeId,
          grateStyleId: current.grateStyleId,
          grateDiameter: current.grateDiameter,
          frameClearOpeningDiameter: current.frameClearOpeningDiameter,
          grateSizeLeftToRight: current.grateSizeLeftToRight,
          grateSizeFrontToBack: current.grateSizeFrontToBack,
          clearOpeningLeftToRight: current.clearOpeningLeftToRight,
          clearOpeningFrontToBack: current.clearOpeningFrontToBack,
          grateSizeLongSpan: current.grateSizeLongSpan,
          grateSizeShortSpan: current.grateSizeShortSpan,
          clearOpeningLongSpan: current.clearOpeningLongSpan,
          clearOpeningShortSpan: current.clearOpeningShortSpan,
          curbOpeningWidth: current.curbOpeningWidth,
          catchBasinWallToWallInsideDimension: current.catchBasinWallToWallInsideDimension,
          basinSize: current.basinSize,
          extendedBottomHeavyDuty: current.extendedBottomHeavyDuty,
          extendedBottomDepth: current.extendedBottomDepth,
          extendedBottomWidth: current.extendedBottomWidth,
          foundryId: current.foundryId,
          foundryPartId: current.foundryPartId,
          structureNotes: notesEditedRef.current ? notes.trim() || null : current.structureNotes,
          isDraft: current.isDraft,
      };
      await api.put<StructureDetail>(`/api/v1/structures/${id}`, body);

      if (files.length > 0) {
        const fd = new FormData();
        for (const f of files) fd.append('photos', f);
        const res = await fetch(`/api/v1/structures/${id}/photos`, {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });
        if (!res.ok) {
          const body = await res.text();
          throw new ApiError(res.status, body, body || `Photo upload failed (${res.status})`);
        }
      }

      clearUnsavedChanges();
      await queryClient.invalidateQueries({ queryKey: ['structure'] });
      navigate(structureReportPath(current.hashId, true));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save notes/photos');
      setBusy(false);
    }
  };

  return (
    <div className="container-fluid" data-unsaved-guard>
      <div className="row mt-lg-3 mt-1 justify-content-center">
        <div className="col-12 col-xl-6 col-lg-6">
          <div>
            <h1>Additional Information</h1>
            <p>
              Please enter any additional information you can to assist us in finding the right part you need.
            </p>
          </div>

          {error && (
            <div className="alert alert-warning mt-2" role="alert">
              {error}
            </div>
          )}

          <div className="pt-2">
            <div className="mb-2">
              <label className="form-label fs-5" htmlFor="StructureNickname">
                Structure Name (Optional)
                <HoverTooltip
                  text="Give your structure a name to find it easier in the future. Locations, structure purpose, or dates all make good names."
                  maxWidth={260}
                >
                  <img
                    className="ps-2"
                    src="/images/ICON/Icon_Info_Green_Filled_Small.png"
                    alt=""
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </HoverTooltip>
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                name="StructureNickname"
                id="StructureNickname"
                placeholder="Structure Name"
                maxLength={MAX_NICKNAME}
                value={nickname}
                onChange={(e) => {
                  nicknameEditedRef.current = true;
                  setNickname(e.target.value.slice(0, MAX_NICKNAME));
                }}
              />
            </div>

            <div className="mb-2">
              <label className="form-label fs-5" htmlFor="structure_notes">
                Additional Requirements or Notes
              </label>
              <WhatToIncludeLink />
              <textarea
                id="structure_notes"
                className="form-control"
                rows={4}
                maxLength={MAX_NOTES}
                value={notes}
                onChange={(e) => {
                  notesEditedRef.current = true;
                  setNotes(e.target.value.slice(0, MAX_NOTES));
                }}
                placeholder="OPTIONAL: Add any additional information we should know (i.e. special requirements, project details, etc). 160 character maximum."
              />
              <small className="form-text text-muted float-end">
                {notes.length} / {MAX_NOTES}
              </small>
            </div>

            <div className="mb-2">
              <label className="form-label fs-5" htmlFor="photos">
                Additional Photos <small className="text-muted">(Optional, Accepted Formats: JPG, PNG)</small>
              </label>
              <WhatToIncludeLink />
              <input
                ref={inputRef}
                type="file"
                id="photos"
                className="form-control"
                accept="image/jpeg,image/png"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, MAX_PHOTOS))}
              />
              {files.length > 0 && (
                <ul className="list-unstyled small text-muted mt-2">
                  {files.map((f, i) => (
                    <li key={i}>• {f.name} ({Math.round(f.size / 1024)} KB)</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mb-2 mt-3 text-center">
              <button
                type="button"
                onClick={continueToReport}
                disabled={busy}
                className="btn btn-sm btn-primary"
              >
                {busy ? 'Saving…' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
